import { IonSpinner } from "@ionic/react";
import { AnimatePresence, motion } from "motion/react";
import React, { useLayoutEffect, useMemo, useRef, type Dispatch, type SetStateAction } from "react";
import { ITINERARY_PANEL } from "../../../config/itineraryPanel.configuration";
import { UI_MOTION } from "../../../config/uiMotion.configuration";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import ArrowTopIcon from "../../../image/icons/map/details/ArrowTop.svg";
import { type SavedItineraryEntry } from "../../../store/persistence/slices/savedItineraries.slice";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { type SavedListFilterTabId } from "../../../tools/savedItineraryDisplay.tools";
import { type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type ItineraryPricingPreferencesDTO } from "../../../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import { type ContainerProps } from "../../../types/ItineraryPlanner";
import { ItineraryDebugListToolbar } from "../../Debug/itineraryDebug";
import { isUiMotionEnabled, resolveUiEaseOut, resolveUiItineraryPanelStagger } from "../../shared/motion/uiMotion.tools";
import { type PanelResizeLayout } from "../panelResize";
import MyCarProposition from "../pricing/MyCarProposition";
import SavedItinerariesEmptyState from "../savedItinerary/SavedItinerariesEmptyState";
import ItineraryItem from "./ItineraryItem";
import { isTransitItinerary, SAVED_LIST_FILTER_TABS, type TransitPageDirection } from "./itineraryList.tools";
import ItineraryListChromeRow from "./ItineraryListChromeRow";
import ItineraryListFilterTabs from "./ItineraryListFilterTabs";
import NoResults from "./NoResults";
import SavedItineraryAccordionList from "./SavedItineraryAccordionList";
import TransitPageLoader from "./TransitPageLoader";

interface CarDetailsState {
    "tollPrice":  number;
    "distance":   number;
    "totalPrice": number;
}

/** Label of the earlier transit page control (button + loader). */
const EARLIER_TRANSIT_LABEL = "Charger les itinéraires précédents";

/** Label of the later transit page control (button + loader). */
const LATER_TRANSIT_LABEL = "Charger les itinéraires suivants";

/** Label of the earlier transit page loader pill. */
const EARLIER_TRANSIT_LOADING_LABEL = "Chargement des itinéraires précédents…";

/** Label of the later transit page loader pill. */
const LATER_TRANSIT_LOADING_LABEL = "Chargement des itinéraires suivants…";

/** Pop-in targets shared by both transit page buttons (fade + slight scale-up). */
const TRANSIT_PAGE_BUTTON_ENTER_FROM = {
    "opacity": 0,
    "scale":   0.92,
};
const TRANSIT_PAGE_BUTTON_ENTER_TO = {
    "opacity": 1,
    "scale":   1,
};

/** Settled state every itinerary row converges to; only `initial` varies per row. */
const ITINERARY_ROW_SETTLED = {
    "opacity": 1,
    "y":       0,
};

interface ItineraryListPanelViewProps {

    /** Whether dark UI palette is active. */
    "isDarkUi": boolean;

    /** Whether prices display in EUR. */
    "isEuro": boolean;

    /** Shared resize layout measurements from Home. */
    "panelResizeLayout": PanelResizeLayout;

    /** List content mode (search results vs saved itineraries). */
    "itineraryListContentMode": ContainerProps["itineraryListContentMode"];

    /** Closes the list panel (saved mode or search results). */
    "onCloseList": () => void;

    /** Enables itinerary debug tooling in the list panel. */
    "debugItineraries": boolean;

    /** Whether list debug relaunch actions are disabled. */
    "isListDebugActionDisabled": boolean;

    /** Relaunches list segmentation for debug. */
    "onRelaunchListSegmentation": () => void;

    /** Active saved-list filter tab id. */
    "activeSavedFilterTab": SavedListFilterTabId;

    /** Updates the active saved-list filter tab. */
    "onActiveSavedFilterTabChange": Dispatch<SetStateAction<SavedListFilterTabId>>;

    /** Whether the panel shows the saved-itinerary list. */
    "isSavedListMode": boolean;

    /** Whether the saved list shell (accordion or exit presence) should render. */
    "shouldRenderSavedListShell": boolean;

    /** Past temporal bucket entries. */
    "pastDisplayedSavedEntries": SavedItineraryEntry[];

    /** Current temporal bucket entries. */
    "currentDisplayedSavedEntries": SavedItineraryEntry[];

    /** Upcoming temporal bucket entries. */
    "upcomingDisplayedSavedEntries": SavedItineraryEntry[];

    /** Empty-state variant when the saved shell is not rendered. */
    "savedEmptyStateVariant": "noSaved" | "noUpcoming" | "filteredEmpty";

    /** Live search itineraries (pre-filter / mode-aware displayed list). */
    "displayedItineraries": ItineraryDTO[];

    /** Raw search itineraries length used for the empty NoResults branch. */
    "itinerariesCount": number;

    /** Whether the main OD search loader is active. */
    "loading": boolean;

    /** Whether departure/arrival coordinates are usable for search. */
    "hasValidCoordinates": boolean;

    /** Selected departure/arrival inputs for NoResults / MyCar. */
    "selectedInputs": ContainerProps["selectedInputs"];

    /** Whether TravelProgress should animate its segment reveal. */
    "travelProgressRevealActive": boolean;

    /** Whether itinerary completion is still loading. */
    "loadingItinerary": boolean;

    /** Whether pricing evaluation is still loading. */
    "loadingPricing": boolean;

    /** Loads one transit results page in the given direction (`earlier`/`later`). */
    "onLoadTransitPage": ( direction: TransitPageDirection ) => void;

    /** Direction of the in-flight transit page request, if any. */
    "transitPageLoading": TransitPageDirection | null;

    /** Whether no earlier transit page remains to be loaded. */
    "transitPageExhaustedEarlier": boolean;

    /** Whether no later transit page remains to be loaded. */
    "transitPageExhaustedLater": boolean;

    /** Ids of paged transit items whose pricing evaluation is still in flight. */
    "transitPagePricingPendingIds": ReadonlySet<string>;

    /** Ids (display order) + direction of the last merged transit page; drives row mount animation. */
    "transitPageNewBatch": {
        "orderedIds": readonly string[];
        "direction":  TransitPageDirection;
    } | null;

    /** Pricing preferences for row chrome. */
    "pricingPreferences": ItineraryPricingPreferencesDTO;

    /** Opens itinerary details for a row. */
    "onItineraryClick": ( itinerary: ItineraryDTO ) => void;

    /** Optional long-press handler (dev review modal). */
    "onItineraryLongPress"?: ( itinerary: ItineraryDTO ) => void;

    /** Removes one saved itinerary. */
    "onRemoveSavedItinerary": ( itineraryId: string ) => void;

    /** Called when saved-list presence exit finishes. */
    "onSavedListExitComplete": () => void;

    /** MYCAR proposition itinerary, when available. */
    "ITakeMyCarData": ContainerProps["ITakeMyCarData"];

    /** Wear cost per km used by the MyCar price pill. */
    "wearCostPerKmChf": number;

    /** Resets the panel to the expanded snap tier. */
    "onResetPanelExpanded": () => void;

    /** Whether the car price detail sheet is open. */
    "isCarPriceOpen": boolean;

    /** Opens/closes the car price detail sheet. */
    "setIsCarPriceOpen": Dispatch<SetStateAction<boolean>>;

    /** Computed car toll/distance/total details. */
    "carDetails": CarDetailsState | null;

    /** Updates car toll/distance/total details. */
    "setCarDetails": Dispatch<SetStateAction<CarDetailsState | null>>;

    /** Switches the panel into MyCar selection mode. */
    "setITakeMyCar": Dispatch<SetStateAction<boolean>>;
}

/**
 * Presentational itinerary list panel (chrome, filters, saved/search content, MyCar footer).
 *
 * @param props List panel composition props.
 * @returns List panel layout root.
 */
const ItineraryListPanelView: React.FC<ItineraryListPanelViewProps> = ( {
    isDarkUi,
    isEuro,
    panelResizeLayout,
    itineraryListContentMode,
    onCloseList,
    debugItineraries,
    isListDebugActionDisabled,
    onRelaunchListSegmentation,
    activeSavedFilterTab,
    onActiveSavedFilterTabChange,
    isSavedListMode,
    shouldRenderSavedListShell,
    pastDisplayedSavedEntries,
    currentDisplayedSavedEntries,
    upcomingDisplayedSavedEntries,
    savedEmptyStateVariant,
    displayedItineraries,
    itinerariesCount,
    loading,
    hasValidCoordinates,
    selectedInputs,
    travelProgressRevealActive,
    loadingItinerary,
    loadingPricing,
    onLoadTransitPage,
    transitPageLoading,
    transitPageExhaustedEarlier,
    transitPageExhaustedLater,
    transitPageNewBatch,
    transitPagePricingPendingIds,
    pricingPreferences,
    onItineraryClick,
    onItineraryLongPress,
    onRemoveSavedItinerary,
    onSavedListExitComplete,
    ITakeMyCarData,
    wearCostPerKmChf,
    onResetPanelExpanded,
    isCarPriceOpen,
    setIsCarPriceOpen,
    carDetails,
    setCarDetails,
    setITakeMyCar,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );

    /**
     * Tinted pill style for transit paging controls: brand border/text on a neutral
     * idle-selector background so the label stays readable in light mode.
     */
    const transitPageButtonStyle: React.CSSProperties = {
        "borderColor":     palette.brandPrimary,
        "backgroundColor": palette.selectorIdleBackground,
        "color":           palette.brandPrimary,
    };

    const scrollContainerRef = useRef<HTMLDivElement | null>( null );
    const earlierControlWrapperRef = useRef<HTMLDivElement | null>( null );

    /**
     * Viewport anchor captured before an `earlier` page request: the first rendered item's
     * DOM id and viewport position, plus scroll metrics as fallback. Consumed to restore
     * the viewport once the prepended items mount.
     */
    const earlierPrependAnchorRef = useRef<{
        "elementId":    string | null;
        "viewportTop":  number;
        "scrollHeight": number;
    } | null>( null );

    /**
     * Whether the "hide the earlier-load control" initial scroll offset was applied for the
     * current result set. Reset whenever the list empties (new search).
     */
    const initialScrollAppliedRef = useRef( false );

    const hasTransitResults = !isSavedListMode && displayedItineraries.some( isTransitItinerary );
    const showEarlierTransitControl = hasTransitResults && !transitPageExhaustedEarlier;
    const showLaterTransitControl = hasTransitResults && !transitPageExhaustedLater;

    /** Itinerary id → position within the last merged transit page (stagger index). */
    const transitPageNewIndexById = useMemo( () => {
        const indexById = new Map<string, number>();
        transitPageNewBatch?.orderedIds.forEach( ( itineraryId, orderIndex ) => {
            indexById.set( itineraryId, orderIndex );
        } );

        return indexById;
    }, [ transitPageNewBatch ] );

    /**
     * The earlier-load control lives above the first item; offset the initial scroll position
     * by its height so it stays hidden until the user scrolls back to the top.
     */
    useLayoutEffect( () => {
        const container = scrollContainerRef.current;
        if ( container === null ) {
            return;
        }
        if ( displayedItineraries.length === 0 ) {
            initialScrollAppliedRef.current = false;

            return;
        }
        if ( !initialScrollAppliedRef.current && hasTransitResults ) {
            container.scrollTop = earlierControlWrapperRef.current?.offsetHeight ?? 0;
            initialScrollAppliedRef.current = true;
        }
    }, [ displayedItineraries, hasTransitResults ] );

    /**
     * After earlier results are prepended, restore the previously-first item at its
     * captured viewport position (falling back to the scrollHeight delta) so the list
     * does not jump to the new top.
     *
     * @remarks Other `itineraries` writes (pricing/offer patches landing while the
     * earlier chain is still running) also change `displayedItineraries`; the anchor
     * must survive those and only be consumed once the first rendered item actually
     * differs — the signature of a real prepend.
     */
    useLayoutEffect( () => {
        const container = scrollContainerRef.current;
        const anchor = earlierPrependAnchorRef.current;
        if ( container === null || anchor === null ) {
            return;
        }

        const firstItem = container.querySelector<HTMLElement>( "[id^='itinerary-list-item-']" );
        if ( firstItem === null ) {
            // List emptied (new search/reset): drop the anchor, leave the scroll alone.
            earlierPrependAnchorRef.current = null;

            return;
        }
        if ( firstItem.id === anchor.elementId ) {
            // Same first item: this commit changed content but prepended nothing — keep waiting.
            return;
        }
        earlierPrependAnchorRef.current = null;

        const anchorElement = anchor.elementId === null ? null : document.getElementById( anchor.elementId );
        if ( anchor.elementId !== null && anchorElement === null ) {
            /*
             * The captured row vanished without an empty-list phase (list replaced): nothing
             * reliable to measure against — leave the scroll alone.
             */
            return;
        }
        if ( anchorElement !== null ) {
            container.scrollTop += anchorElement.getBoundingClientRect().top - anchor.viewportTop;
        } else {
            container.scrollTop += container.scrollHeight - anchor.scrollHeight;
        }
    }, [ displayedItineraries ] );

    /**
     * Drops a pending prepend compensation when the page load ends without new items
     * (error, abort, or exhausted window).
     */
    useLayoutEffect( () => {
        if ( transitPageLoading === null ) {
            earlierPrependAnchorRef.current = null;
        }
    }, [ transitPageLoading ] );

    /**
     * Loads the previous transit page; captures the first item's viewport position first
     * so it can be restored after the prepend. The hook rejects the request while another
     * page is in flight, so no anchor is armed in that case.
     */
    const handleLoadEarlierTransitPageClick = (): void => {
        const container = scrollContainerRef.current;
        if ( container !== null && transitPageLoading === null ) {
            const firstItem = container.querySelector<HTMLElement>( "[id^='itinerary-list-item-']" );
            earlierPrependAnchorRef.current = {
                "elementId":    firstItem?.id ?? null,
                "viewportTop":  firstItem?.getBoundingClientRect().top ?? 0,
                "scrollHeight": container.scrollHeight,
            };
        }
        onLoadTransitPage( "earlier" );
    };

    /**
     * Loads the next transit page; appended items do not shift the viewport.
     */
    const handleLoadLaterTransitPageClick = (): void => {
        onLoadTransitPage( "later" );
    };

    return (
        <div
            className="flex min-h-0 flex-1 flex-col"
            id="itinerary-list-root"
        >
            <ItineraryListChromeRow
                contentMode={itineraryListContentMode}
                isDarkUi={isDarkUi}
                onCloseList={onCloseList}
                panelResizeLayout={panelResizeLayout}
            />
            <div
                className="flex min-h-0 flex-1 flex-col p-[5px] pt-0"
                id="itinerary-list-content"
            >
                <ItineraryDebugListToolbar
                    debugItineraries={debugItineraries}
                    isDarkUi={isDarkUi}
                    isDisabled={isListDebugActionDisabled}
                    onRelaunch={onRelaunchListSegmentation}
                />
                {isSavedListMode && ITINERARY_PANEL.enableSavedItineraryListFilters
                    ? (
                        <ItineraryListFilterTabs
                            activeTabId={activeSavedFilterTab}
                            className="mb-2"
                            id="saved-list-filter-tabs"
                            isDarkUi={isDarkUi}
                            onActiveTabChange={onActiveSavedFilterTabChange}
                            tabs={SAVED_LIST_FILTER_TABS}
                        />
                    )
                    : null}
                {isSavedListMode
                    ? shouldRenderSavedListShell
                        ? (
                            <SavedItineraryAccordionList
                                currentEntries={currentDisplayedSavedEntries}
                                isDarkUi={isDarkUi}
                                isEuro={isEuro}
                                onItineraryClick={onItineraryClick}
                                onRemoveSavedItinerary={onRemoveSavedItinerary}
                                onSavedListExitComplete={onSavedListExitComplete}
                                pastEntries={pastDisplayedSavedEntries}
                                pricingPreferences={pricingPreferences}
                                upcomingEntries={upcomingDisplayedSavedEntries}
                            />
                        )
                        : (
                            <SavedItinerariesEmptyState
                                isDarkUi={isDarkUi}
                                variant={savedEmptyStateVariant}
                            />
                        )

                    : itinerariesCount === 0 && !loading && hasValidCoordinates
                        ? (
                            <NoResults
                                arrivalLat={selectedInputs.arrival.lat}
                                arrivalLon={selectedInputs.arrival.lon}
                                departureLat={selectedInputs.departure.lat}
                                departureLon={selectedInputs.departure.lon}
                            />
                        )
                        : (
                            <div
                                className="flowrideScrollBar min-h-0 flex-1 overflow-auto scroll-container [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                                id="itinerary-list-scroll"
                                ref={scrollContainerRef}
                                style={{ "overflowAnchor": "none" }}
                            >
                                <div
                                    className="flex flex-col gap-2"
                                    id="itinerary-list-items"
                                >
                                    {showEarlierTransitControl && (
                                        <div
                                            className="flex h-10 items-center justify-center"
                                            id="itinerary-list-earlier-control"
                                            ref={earlierControlWrapperRef}
                                        >
                                            <AnimatePresence
                                                initial={false}
                                                mode="wait"
                                            >
                                                {transitPageLoading === "earlier"
                                                    ? (
                                                        <TransitPageLoader
                                                            id="itinerary-list-earlier-loading"
                                                            isDarkUi={isDarkUi}
                                                            key="earlier-loader"
                                                            label={EARLIER_TRANSIT_LOADING_LABEL}
                                                        />
                                                    )
                                                    : (
                                                        <motion.button
                                                            animate={TRANSIT_PAGE_BUTTON_ENTER_TO}
                                                            aria-label={EARLIER_TRANSIT_LABEL}
                                                            className="inline-flex items-center gap-1.5 rounded-full border-2 border-solid px-3.5 py-1.5 text-xs font-bold shadow-sm transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                                                            exit={animationsEnabled ? TRANSIT_PAGE_BUTTON_ENTER_FROM : undefined}
                                                            id="itinerary-list-earlier-button"
                                                            initial={animationsEnabled ? TRANSIT_PAGE_BUTTON_ENTER_FROM : false}
                                                            key="earlier-button"
                                                            onClick={handleLoadEarlierTransitPageClick}
                                                            style={transitPageButtonStyle}
                                                            type="button"
                                                            whileTap={animationsEnabled ? { "scale": 0.94 } : undefined}
                                                        >
                                                            <span
                                                                aria-hidden={true}
                                                                className="inline-block h-3 w-3 shrink-0"
                                                                style={{
                                                                    "backgroundColor": "currentColor",
                                                                    "mask":            `url(${ ArrowTopIcon }) center / contain no-repeat`,
                                                                    "WebkitMask":      `url(${ ArrowTopIcon }) center / contain no-repeat`,
                                                                }}
                                                            />
                                                            { EARLIER_TRANSIT_LABEL }
                                                        </motion.button>
                                                    )}
                                            </AnimatePresence>
                                        </div>
                                    )}
                                    {displayedItineraries.map( ( itinerary, index ) => {
                                        const newItemIndex = transitPageNewIndexById.get( itinerary.id ) ?? null;
                                        const mountsWithEnterAnimation = newItemIndex !== null && animationsEnabled;

                                        // "earlier" rows drop in from above the list, "later" rows rise from below.
                                        const rowInitial = mountsWithEnterAnimation
                                            ? {
                                                "opacity": 0,
                                                "y":       transitPageNewBatch?.direction === "earlier" ? -14 : 14,
                                            }
                                            : false;

                                        return (
                                            <motion.div
                                                animate={ITINERARY_ROW_SETTLED}
                                                initial={rowInitial}
                                                key={itinerary.id ?? index}
                                                transition={{
                                                    ...resolveUiEaseOut( reducedMotion, UI_MOTION.duration.fastMs ),
                                                    "delay": mountsWithEnterAnimation
                                                        ? resolveUiItineraryPanelStagger( newItemIndex ?? 0, reducedMotion )
                                                        : 0,
                                                }}
                                            >
                                                <ItineraryItem
                                                    animateTravelProgress={travelProgressRevealActive}
                                                    handleItineraryClick={onItineraryClick}
                                                    index={index}
                                                    isDarkUi={isDarkUi}
                                                    isEuro={isEuro}
                                                    isPriceLoading={loadingPricing || transitPagePricingPendingIds.has( itinerary.id )}
                                                    itinerary={itinerary}
                                                    onItineraryLongPress={onItineraryLongPress}
                                                    pricingPreferences={pricingPreferences}
                                                />
                                            </motion.div>
                                        );
                                    } )}
                                    {showLaterTransitControl && (
                                        <div
                                            className="flex h-10 items-center justify-center"
                                            id="itinerary-list-later-control"
                                        >
                                            <AnimatePresence
                                                initial={false}
                                                mode="wait"
                                            >
                                                {transitPageLoading === "later"
                                                    ? (
                                                        <TransitPageLoader
                                                            id="itinerary-list-later-loading"
                                                            isDarkUi={isDarkUi}
                                                            key="later-loader"
                                                            label={LATER_TRANSIT_LOADING_LABEL}
                                                        />
                                                    )
                                                    : (
                                                        <motion.button
                                                            animate={TRANSIT_PAGE_BUTTON_ENTER_TO}
                                                            aria-label={LATER_TRANSIT_LABEL}
                                                            className="inline-flex items-center gap-1.5 rounded-full border-2 border-solid px-3.5 py-1.5 text-xs font-bold shadow-sm transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                                                            exit={animationsEnabled ? TRANSIT_PAGE_BUTTON_ENTER_FROM : undefined}
                                                            id="itinerary-list-later-button"
                                                            initial={animationsEnabled ? TRANSIT_PAGE_BUTTON_ENTER_FROM : false}
                                                            key="later-button"
                                                            onClick={handleLoadLaterTransitPageClick}
                                                            style={transitPageButtonStyle}
                                                            type="button"
                                                            whileTap={animationsEnabled ? { "scale": 0.94 } : undefined}
                                                        >
                                                            <span
                                                                aria-hidden={true}
                                                                className="inline-block h-3 w-3 shrink-0"
                                                                style={{
                                                                    "backgroundColor": "currentColor",
                                                                    "mask":            `url(${ ArrowTopIcon }) center / contain no-repeat`,
                                                                    "WebkitMask":      `url(${ ArrowTopIcon }) center / contain no-repeat`,

                                                                    // Same chevron asset as the earlier button, flipped downward.
                                                                    "transform": "rotate(180deg)",
                                                                }}
                                                            />
                                                            { LATER_TRANSIT_LABEL }
                                                        </motion.button>
                                                    )}
                                            </AnimatePresence>
                                        </div>
                                    )}
                                    {loadingItinerary && (
                                        <div
                                            className="text-center bold flex justify-center h-10"
                                            id="itinerary-list-loading"
                                        >
                                            Chargement des itinéraires en cours&nbsp;
                                            <IonSpinner
                                                className="h-1 w-5 mt-[-2px]"
                                                color="primary"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                {!isSavedListMode && ITakeMyCarData != null && (
                    <MyCarProposition
                        ITakeMyCarData={ITakeMyCarData}
                        carDetails={carDetails}
                        isCarPriceOpen={isCarPriceOpen}
                        isDarkUi={isDarkUi}
                        isEuro={isEuro}
                        onResetPanelExpanded={onResetPanelExpanded}
                        selectedInputs={selectedInputs}
                        setCarDetails={setCarDetails}
                        setITakeMyCar={setITakeMyCar}
                        setIsCarPriceOpen={setIsCarPriceOpen}
                        wearCostPerKmChf={wearCostPerKmChf}
                    />
                )}
            </div>
        </div>
    );
};

export default ItineraryListPanelView;
